/**
 * LES RUBRIQUES D'IDENTIFICATION D'UN ÉQUIPEMENT (§6.4.2) — côté écran.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * JUMEAU de `backend/src/rubriques-equipement.ts`, et il faut savoir pourquoi
 * ils sont deux plutôt qu'un.
 *
 * Le moteur POSE le drapeau `ficheComplete` : c'est lui qui fait foi, et il le
 * recalcule à chaque écriture — aucune case ne se coche à la main. L'écran, lui,
 * doit NOMMER ce qui manque, sur une fiche qu'il a déjà en mémoire, sans
 * demander au serveur une seconde fois ce qu'il vient de recevoir. Les deux
 * répondent donc à la même question à deux moments différents.
 *
 * ⚠️ LES DEUX LISTES DOIVENT RESTER IDENTIQUES, et ce n'est pas une consigne :
 * `backend/tests/front-rubriques-equipement.test.js` charge les DEUX modules et
 * les compare clé à clé — les deux listes, celle de l'objet comme celle du
 * logiciel. Une rubrique ajoutée d'un seul côté fait échouer la suite — parce
 * qu'une divergence ici est invisible autrement : l'écran nommerait cinq manques
 * quand le drapeau en compte six, et personne ne comprendrait pourquoi la fiche
 * reste marquée incomplète après avoir « tout » rempli.
 *
 * ── Le défaut d'origine (RQ, 11/09/2026) ───────────────────────────────────
 *
 * « Si une fiche équipement est incomplète, il faut me montrer quels champs
 *  sont incomplets pour lever le drapeau. »
 *
 * Le bandeau disait « des rubriques exigées manquent » — sur **224 fiches sur
 * 248**. Un signalement qui s'affiche sur 90 % du parc sans jamais dire quoi
 * faire n'est plus un signalement, c'est un décor.
 *
 * ── DEUX LISTES DEPUIS LE 05/10/2026 ───────────────────────────────────────
 *
 * Une fiche de nature `logiciel` relève d'une AUTRE liste : un logiciel n'a ni
 * modèle, ni salle. Le raisonnement complet est dans le module serveur ; ici,
 * seule compte la règle — la liste se choisit d'après `e.nature`, et l'écran ne
 * la passe jamais en paramètre.
 */

export interface RubriqueExigee {
  cle: string;
  label: string;
  /** Ce que son absence EMPÊCHE. Jamais « champ obligatoire » : cela n'explique
   *  rien, et se lit comme une contrainte d'outil plutôt que de métier. */
  pourquoi: string;
  /** Comment on sait qu'elle est renseignée, quand ce n'est pas simplement
   *  « une chaîne non vide à `cle` » — la version est imbriquée, et « où ça
   *  tourne » se satisfait de deux façons. */
  renseignee?: (e: Record<string, unknown>) => boolean;
}

const txt = (v: unknown) => String(v ?? '').trim();

/** Les six rubriques du §6.4.2 pour un OBJET, dans l'ordre où on les rencontre
 *  sur la fiche. */
export const RUBRIQUES_EXIGEES: RubriqueExigee[] = [
  { cle: 'marque', label: 'Marque', pourquoi: 'Sans elle, deux appareils du même type ne se distinguent pas au parc.' },
  { cle: 'modele', label: 'Modèle', pourquoi: 'Une vérification de méthode vaut pour UN modèle : sans lui, elle ne se rattache à rien.' },
  { cle: 'numeroSerie', label: 'N° de série', pourquoi: 'C’est l’identifiant qui suit l’appareil, y compris après un déménagement ou un prêt.' },
  { cle: 'localisation', label: 'Localisation', pourquoi: 'Un auditeur demande à VOIR l’appareil ; une fiche qui ne dit pas où il est ne se vérifie pas.' },
  { cle: 'fournisseur', label: 'Fournisseur', pourquoi: 'C’est lui qu’on appelle en panne, et lui qui porte le contrat de maintenance.' },
  { cle: 'dateMiseEnService', label: 'Date de mise en service', pourquoi: 'Elle ouvre la fiche de vie : sans elle, aucune périodicité ne se calcule.' },
];

/** Les ordinateurs déclarés d'une fiche, quelle que soit la forme reçue. */
const ordinateursDe = (e: Record<string, unknown>): string[] =>
  (Array.isArray(e.ordinateursCodes) ? e.ordinateursCodes : []).map(txt).filter(Boolean);

/** Les six rubriques du §6.4.2 pour un LOGICIEL (05/10/2026). */
export const RUBRIQUES_LOGICIEL: RubriqueExigee[] = [
  { cle: 'marque', label: 'Éditeur', pourquoi: 'C’est lui qui publie les versions, et c’est son nom qu’on cherche pour savoir si celle installée est encore soutenue.' },
  {
    cle: 'logiciel.version',
    label: 'Version installée',
    pourquoi: 'Une vérification de méthode vaut pour LA version sous laquelle elle a été conduite : sans elle, aucune vérification ne se rattache à rien.',
    renseignee: (e) => txt((e.logiciel as Record<string, unknown> | null | undefined)?.version) !== '',
  },
  { cle: 'numeroSerie', label: 'N° de licence / clé', pourquoi: 'C’est ce qui prouve que le laboratoire a le droit de s’en servir, et ce qu’on donne à l’éditeur pour obtenir une mise à jour.' },
  {
    cle: 'ordinateursCodes',
    label: 'Où il tourne',
    pourquoi: 'Un auditeur demande à VOIR le logiciel tourner : désignez les ordinateurs où il est installé, ou déclarez-le accessible en ligne depuis tous.',
    renseignee: (e) => e.accesEnLigne === true || ordinateursDe(e).length > 0,
  },
  { cle: 'fournisseur', label: 'Fournisseur', pourquoi: 'C’est lui qu’on appelle quand le logiciel refuse de démarrer, et lui qui porte le contrat de maintenance.' },
  { cle: 'dateMiseEnService', label: 'Date de mise en service', pourquoi: 'Elle ouvre la fiche de vie : sans elle, aucune périodicité de maintenance ne se calcule.' },
];

export const CLES_EXIGEES = RUBRIQUES_EXIGEES.map((r) => r.cle);
export const CLES_LOGICIEL = RUBRIQUES_LOGICIEL.map((r) => r.cle);

/** La liste qui s'applique à cette NATURE de fiche. Toute autre valeur —
 *  inconnue ou absente — relève de la liste de l'objet, comme au serveur. */
export const rubriquesExigees = (nature?: unknown): RubriqueExigee[] =>
  (txt(nature) === 'logiciel' ? RUBRIQUES_LOGICIEL : RUBRIQUES_EXIGEES);

const estRenseignee = (r: RubriqueExigee, e: Record<string, unknown>): boolean =>
  (r.renseignee ? r.renseignee(e) : txt(e[r.cle]) !== '');

/** Ce qui manque à cette fiche, dans l'ordre de la fiche. Vide = complète. */
export function rubriquesManquantes(e: Record<string, unknown> | null | undefined): RubriqueExigee[] {
  if (!e) return [];
  return rubriquesExigees(e.nature).filter((r) => !estRenseignee(r, e));
}
